import * as React from "react";
import { ChevronDown } from "@/components/icons";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { ScrollFadeArea } from "./ScrollFadeArea";

export interface BulkSelectedGroupItem {
  key: string;
  label: string;
}

export interface BulkSelectedGroup {
  key: string;
  /** 그룹 제목 (예: 충전소명) */
  label: string;
  /** 우측에 "N기" 로 보이는 수. 뱃지 수와 다를 수 있어 따로 받는다 */
  count: number;
  /** 펼치면 뱃지로 나열되는 항목 (예: 충전기 관리번호) */
  items: BulkSelectedGroupItem[];
}

export interface BulkSelectedGroupListProps {
  /** "선택한 충전기 N기" 같은 섹션 제목 */
  heading: React.ReactNode;
  groups: BulkSelectedGroup[];
  /** 그룹 목록을 서버에서 받아오는 동안 스켈레톤을 그린다 */
  isLoading?: boolean;
  /** 항목이 하나도 없는 그룹을 펼쳤을 때 보일 문구 */
  emptyItemsLabel?: string;
  /** 스크롤 영역 높이 제한 (기본 max-h-60) */
  scrollClassName?: string;
}

/**
 * 일괄 변경 모달 상단의 "선택한 N" 섹션. 그룹별로 접고 펼 수 있고, 처음엔 첫 그룹만 펼친다.
 * 그룹이 비동기로 채워져도 첫 그룹이 펼쳐지도록, 사용자가 만지기 전(null)에는 첫 그룹을
 * 펼친 것으로 취급한다
 */
export function BulkSelectedGroupList({
  heading,
  groups,
  isLoading = false,
  emptyItemsLabel = "-",
  scrollClassName,
}: BulkSelectedGroupListProps) {
  const [expanded, setExpanded] = React.useState<Set<string> | null>(null);
  const firstKey = groups[0]?.key;
  const isExpanded = (key: string) =>
    expanded ? expanded.has(key) : key === firstKey;

  const toggleExpanded = (key: string) => {
    setExpanded((prev) => {
      const next = new Set(prev ?? (firstKey ? [firstKey] : []));
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  return (
    <div className="flex flex-col gap-3 w-full">
      <div className="typo-heading-2-semibold text-labels-primary">
        {heading}
      </div>
      <ScrollFadeArea
        wrapperClassName="w-full"
        className={cn("max-h-60 [scrollbar-gutter:stable]", scrollClassName)}
      >
        {isLoading ? (
          <div className="flex flex-col gap-3">
            {Array.from({ length: 3 }, (_, i) => (
              <Skeleton key={i} className="h-6 w-full rounded" />
            ))}
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {groups.map((group) => {
              const open = isExpanded(group.key);
              return (
                <div key={group.key} className="flex flex-col">
                  <button
                    type="button"
                    onClick={() => toggleExpanded(group.key)}
                    className="flex items-center justify-between w-full"
                  >
                    <span className="typo-body-1-medium text-labels-secondary">
                      {group.label}
                    </span>
                    <span className="flex items-center gap-2">
                      <span className="typo-body-1-medium text-primary-normal">
                        {group.count}기
                      </span>
                      <ChevronDown
                        size={24}
                        color="labels-secondary"
                        className={cn(
                          "transition-transform",
                          open && "rotate-180",
                        )}
                      />
                    </span>
                  </button>
                  <div
                    className={cn(
                      "grid transition-[grid-template-rows] duration-200 ease-in-out",
                      open ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
                    )}
                  >
                    <div className="overflow-hidden">
                      <div className="flex flex-wrap gap-2 items-center bg-fills-light inset-border-normal rounded-xl px-3 py-4 mt-3">
                        {group.items.length === 0 ? (
                          <span className="typo-label-1-medium text-labels-assistive">
                            {emptyItemsLabel}
                          </span>
                        ) : (
                          group.items.map((item) => (
                            <span
                              key={item.key}
                              className="inline-flex items-center rounded-full bg-labels-secondary/10 inset-border-double px-2.5 py-1 typo-label-1-semibold text-labels-secondary"
                            >
                              {item.label}
                            </span>
                          ))
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </ScrollFadeArea>
    </div>
  );
}

BulkSelectedGroupList.displayName = "BulkSelectedGroupList";
